'use client';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { InstanceWordmark } from '../components/ui/InstanceWordmark';
import { useTranslation } from '../lib/i18n';
import { usePluginUi } from '../lib/queries';
import { usePagePath } from '../lib/usePagePath';
import { pluginListingForPath } from '../lib/sameDocumentNavigation';

// RouteTransition in the persistent Shell owns the enter/exit choreography. The template deliberately
// stays neutral so the incoming page does not play a second, much faster fade on top of it.
export default function Template({ children }: { children: ReactNode }) {
  // The path of the page actually mounted, not the address in the bar: while an intercepted overlay
  // (/settings, /account) is open the address belongs to the overlay and arriving straight at one has
  // no page under it at all (null). The word closes the mounted page, so it follows the shell's one
  // `usePagePath` rather than restating the rule — Settings over /chat adds no word under the chat,
  // and a direct /settings visit shows none under the overlay. /chat itself owns its bottom edge
  // through the fixed surface, visual viewport and safe-area policy, so the conversation page closes
  // without the word, and so does a plugin page whose manifest says `web.wordmark: false` (a full-height
  // application surface such as the editor).
  const { locale } = useTranslation();
  const listing = usePluginUi(locale).data;
  const pagePath = usePagePath(listing);
  const showWordmark = pagePath !== null && pagePath !== '/chat'
    && pluginListingForPath(pagePath, listing)?.wordmark !== false;
  // A slot inside the persistent overlay must not interrupt the deck's flex-height chain.
  return (
    <div className="h-full in-[[data-page-overlay-slot]]:contents">
      {children}
      {/* The instance name closing every page, mounted here rather than by each page: a new page, core
          or plugin, gets it without doing anything. This wrapper is the one place that works — inside
          the shell's `h-full` chain, where the flex slack the column stretches in sits BELOW the word
          and the floor math fills exactly it. Placed after the chain instead, the word is pushed below
          that slack and its floor height feeds back into the stretch, a measure loop the browser
          freezes mid-way. The spacing matches the dashboard's old own copy (mt-16, @sm:mt-24); on the
          floor the stylesheet drops it and counts it into the measured room instead. */}
      {showWordmark ? <PageWordmark /> : null}
    </div>
  );
}

/** The wordmark exactly where the template puts it — but only in the PAGE tree. Parallel-slot trees
 *  (the @pageOverlay default and intercepted overlays) pass through this same template outside the
 *  page's `<main>` and compute the same page path, so the address cannot tell the two instances
 *  apart: the DOM position is the structural distinction that can. Decided in a layout effect,
 *  before paint, so neither tree ever flashes the other's answer. */
function PageWordmark() {
  const ref = useRef<HTMLDivElement>(null);
  const [inPage, setInPage] = useState(false);
  useLayoutEffect(() => {
    setInPage(ref.current?.closest('main') !== null);
  }, []);
  return (
    <div ref={ref} className="contents">
      {inPage ? <InstanceWordmark className="mt-16 @sm:mt-24" /> : null}
    </div>
  );
}
